import { Project, ResourceAllocation, ResourceRoleType, Holiday, Resource, Customer, TimesheetEntry, ProjectProfitability, NonBillableSummary, TimeOffRequest } from '../types'
import { dateCalculationService } from './dateCalculationService'
import { currencyService } from './currencyService'
import { allocationDistributionService } from './allocationDistributionService'

class ReportingService {
  getProjectProfitability(
    projects: Project[], resources: Resource[], allocations: ResourceAllocation[],
    resourceRoleTypes: ResourceRoleType[], holidays: Holiday[], customers: Customer[],
    timesheets: TimesheetEntry[], timeOffRequests: TimeOffRequest[] = []
  ): ProjectProfitability[] {
    return projects.map(project => {
      let projectRevenueUSD = 0
      let projectCostUSD = 0
      const customer = customers.find(c => c.id === project.customer_id)
      const projectAllocations = allocations.filter(a => a.project_id === project.id)

      projectAllocations.forEach(allocation => {
        const costRate = allocation.cost_rate_at_allocation || resourceRoleTypes.find(rt => rt.id === allocation.role_type_id)?.cost_rate || 0
        const billingRate = allocation.billing_rate_at_allocation || resourceRoleTypes.find(rt => rt.id === allocation.role_type_id)?.billing_rate || 0
        const allocationCurrency = allocation.currency_at_allocation || resourceRoleTypes.find(rt => rt.id === allocation.role_type_id)?.currency || 'USD'
        const timeOffDates = dateCalculationService.getResourceTimeOffDateSet(allocation.resource_id, timeOffRequests, allocation.allocation_start, allocation.allocation_end)
        const totalHours = allocationDistributionService.calculateTotalHours(
          allocation.allocation_start,
          allocation.allocation_end,
          allocation.daily_hours,
          allocation.allocation_interval,
          holidays,
          timeOffDates
        )
        projectCostUSD += currencyService.convert(costRate * totalHours, allocationCurrency, 'USD').convertedAmount
        if (allocation.billable) {
          projectRevenueUSD += currencyService.convert(billingRate * totalHours, allocationCurrency, 'USD').convertedAmount
        }
      })

      const totalProfit = projectRevenueUSD - projectCostUSD
      return {
        projectId: project.id,
        projectTitle: project.title,
        customerName: customer?.title || 'N/A',
        totalRevenue: projectRevenueUSD,
        totalCost: projectCostUSD,
        totalProfit,
        profitMargin: projectRevenueUSD > 0 ? (totalProfit / projectRevenueUSD) * 100 : 0,
        currency: 'USD',
        status: project.status,
      } as ProjectProfitability
    })
  }

  getNonBillableSummary(
    projects: Project[], resources: Resource[], allocations: ResourceAllocation[],
    resourceRoleTypes: ResourceRoleType[], holidays: Holiday[], timesheets: TimesheetEntry[],
    timeOffRequests: TimeOffRequest[] = []
  ): NonBillableSummary[] {
    const summaries: NonBillableSummary[] = []

    allocations.forEach(allocation => {
      if (!allocation.billable) {
        const project = projects.find(p => p.id === allocation.project_id)
        const resource = resources.find(r => r.id === allocation.resource_id)
        const costRate = allocation.cost_rate_at_allocation || resourceRoleTypes.find(rt => rt.id === allocation.role_type_id)?.cost_rate || 0
        const allocationCurrency = allocation.currency_at_allocation || resourceRoleTypes.find(rt => rt.id === allocation.role_type_id)?.currency || 'USD'
        if (!project || !resource) return
        const timeOffDates = dateCalculationService.getResourceTimeOffDateSet(allocation.resource_id, timeOffRequests, allocation.allocation_start, allocation.allocation_end)
        const nonBillableHours = allocationDistributionService.calculateTotalHours(
          allocation.allocation_start,
          allocation.allocation_end,
          allocation.daily_hours,
          allocation.allocation_interval,
          holidays,
          timeOffDates
        )
        if (nonBillableHours > 0) {
          summaries.push({
            resourceId: resource.id,
            resourceName: resource.title,
            projectId: project.id,
            projectTitle: project.title,
            nonBillableHours,
            nonBillableCost: currencyService.convert(costRate * nonBillableHours, allocationCurrency, 'USD').convertedAmount,
            currency: 'USD',
          })
        }
      }
    })

    timesheets.forEach(ts => {
      if (ts.non_billable_hours <= 0) return
      const project = projects.find(p => p.id === ts.project_id)
      const resource = resources.find(r => r.id === ts.resource_id)
      if (!project || !resource) return
      const allocation = allocations.find(a => a.project_id === ts.project_id && a.resource_id === ts.resource_id)
      if (!allocation) return
      const roleType = resourceRoleTypes.find(rt => rt.id === allocation.role_type_id)
      if (!roleType) return
      const costRate = allocation.cost_rate_at_allocation || roleType.cost_rate
      if (!costRate) return
      summaries.push({
        resourceId: resource.id,
        resourceName: resource.title,
        projectId: project.id,
        projectTitle: project.title,
        nonBillableHours: ts.non_billable_hours,
        nonBillableCost: currencyService.convert(costRate * ts.non_billable_hours, roleType.currency, 'USD').convertedAmount,
        currency: 'USD',
      })
    })

    const aggregated: Record<string, NonBillableSummary> = {}
    summaries.forEach(s => {
      const key = `${s.resourceId}-${s.projectId}`
      if (!aggregated[key]) aggregated[key] = { ...s }
      else {
        aggregated[key].nonBillableHours += s.nonBillableHours
        aggregated[key].nonBillableCost += s.nonBillableCost
      }
    })

    return Object.values(aggregated)
  }
}

export const reportingService = new ReportingService()
